// Domain types for the SPARK platform. These describe Firestore documents as stored.
// Timestamps are typed structurally so the same types work with the web SDK and the Admin SDK.
// See docs/ARCHITECTURE.md for who may read and write each collection.

import type { Permission, Role } from './permissions.ts'

export interface TimestampLike {
  seconds: number
  nanoseconds: number
  toMillis(): number
  toDate(): Date
}

export type Sport = 'basketball' | 'volleyball'
export type SectionKey = 'basketball' | 'volleyball' | 'quran' | 'bazaar'
export type SectionScope = SectionKey | 'all'
export const SECTION_KEYS: readonly SectionKey[] = ['basketball', 'volleyball', 'quran', 'bazaar']
export const SECTION_LABELS: Record<SectionKey, string> = {
  basketball: 'Basketball',
  volleyball: 'Volleyball',
  quran: 'Quran competition',
  bazaar: 'Bazaar',
}

export type Side = 'home' | 'away'

export type ImportKind = 'basketball' | 'volleyball' | 'vendors' | 'foodVendors' | 'sponsors' | 'quran' | 'divisions'
export const IMPORT_KIND_LABELS: Record<ImportKind, string> = {
  basketball: 'Basketball teams',
  volleyball: 'Volleyball teams',
  vendors: 'Bazaar vendors',
  foodVendors: 'Food vendors',
  sponsors: 'Sponsors',
  quran: 'Quran competition',
  divisions: 'Divisions',
}

/** Where a record came from when it was imported from sparkmidwest.org. Exactly these keys. */
export interface SourceRef {
  system: 'sparkmidwest'
  kind: ImportKind
  id: string
  importedAt: TimestampLike | null
  /** When the record was created on the website, if known. */
  registeredAt?: TimestampLike | null
}

/** `removed` = cancelled, withdrawn, inactive or missing from a full pull. Removed records are never public. */
export type SourceStatus = 'active' | 'waitlisted' | 'pending' | 'removed' | 'manual'

export interface Contact {
  name: string
  email: string
  phone: string
}

// ---------------------------------------------------------------- staff

/** events/{eventId}/staff/{uid}. Written only by Cloud Functions. */
export interface StaffDoc {
  email: string
  displayName: string
  roles: Role[]
  perms: Permission[]
  /** Sections this person may announce to, from the roles that grant announce.send only. */
  announceSections: SectionScope[]
  /** Sections picked for the Announcer role, kept so announceSections can be recomputed. */
  announcerSections: SectionKey[]
  /** Courts this person keeps score on by default (copied into courtKeepers when set). */
  defaultCourts: string[]
  active: boolean
  /** Access ends at this time (rules enforce it). Null means no end date. */
  accessUntil: TimestampLike | null
  invitedBy: string | null
  invitedAt: TimestampLike | null
  updatedAt: TimestampLike | null
}

/** platformAdmins/{uid}. Full access to every event. Written only by scripts. */
export interface PlatformAdminDoc {
  email: string
  displayName: string
  addedAt: TimestampLike | null
}

// ---------------------------------------------------------------- event

export interface Venue {
  id: string
  name: string
  address: string
}

export interface Court {
  id: string
  name: string
  venueId: string
  sport: Sport
  sortOrder: number
  active: boolean
}

export interface EventDay {
  /** YYYY-MM-DD in the event time zone */
  date: string
  label: string
}

export interface EventLinks {
  website: string
  basketballRegistration: string
  volleyballRegistration: string
  quranRegistration: string
  vendorRegistration: string
  foodVendorRegistration: string
  sponsorship: string
  donate: string
  rules: string
  privacyPolicy: string
  /**
   * Where the app's Settings "Report a problem" row goes (Q6, D78): an https page or a plain
   * `mailto:address`, or '' for no row. Absent on events stored before it existed. firestore.rules
   * checks the shape; the app re-checks it with app-core's `safeContactHref` before drawing the row.
   */
  contact?: string
}

export interface EventDoc {
  name: string
  shortName: string
  year: number
  /** YYYY-MM-DD */
  startDate: string
  endDate: string
  timezone: string
  days: EventDay[]
  venues: Venue[]
  courts: Court[]
  sections: Record<SectionKey, { enabled: boolean }>
  links: EventLinks
  notifications: {
    /** Minutes before the expected start to remind followers. 0 turns reminders off. */
    gameReminderMinutes: number
    finalScoreAlerts: boolean
    /** Minimum change in a court delay, in minutes, before followers are told. */
    delayAlertThreshold: number
  }
  /** Default game length used for conflict checks when a game has none. */
  defaultSlotMinutes: number
  /** Set by the first schedule publish. While null, publishes send one "schedule is posted" push. */
  schedulePublishedAt: TimestampLike | null
  /** Hourly import from sparkmidwest.org while true. */
  importSync: { enabled: boolean; lastRunAt: TimestampLike | null }
  /** After this date the purge job strips personal data from this event. */
  purgeAfter: string | null
  /**
   * Whether that purge also removes the public rosters (first name and jersey number) and team
   * photos, with their Storage objects (DECISIONS D67 #4b). Only `false` keeps them: an event that
   * never stored the setting gets the default, removed. The purge stores the value it applies when it
   * starts and again beside `purgedAt`, and the rules freeze it once `purgeStartedAt` or `purgedAt`
   * is set.
   */
  purgeRostersAndPhotos?: boolean
  /**
   * Set by the purge job before it removes anything (D67 #4b). Without `purgedAt`, a purge is under
   * way or died part-way; the next daily run finishes it, whatever `purgeAfter` says by then.
   */
  purgeStartedAt?: TimestampLike | null
  /** Set by the purge job. */
  purgedAt?: TimestampLike | null
  updatedAt: TimestampLike | null
  updatedBy: string | null
}

export type ContentKey = 'home' | 'basketball' | 'volleyball' | 'quran' | 'bazaar'

/** events/{e}/content/{key}: copy the attendee app shows, editable by staff with content.edit. */
export interface ContentDoc {
  headline: string
  body: string
  /** Hours or schedule lines, e.g. "Saturday · 8 AM to 10:30 PM". */
  hours: { label: string; value: string }[]
  timeline: { time: string; title: string; detail: string }[]
  /** Short note such as "Schedule posts Oct 20". */
  notice: string
  links: { label: string; url: string }[]
  updatedAt: TimestampLike | null
}

// ---------------------------------------------------------------- divisions

export type DivisionFormat = 'showcase' | 'group' | 'double_elim' | 'single_elim' | 'round_robin'
export const DIVISION_FORMAT_LABELS: Record<DivisionFormat, string> = {
  showcase: 'Showcase',
  group: 'Group stage',
  double_elim: 'Double elimination',
  single_elim: 'Single elimination',
  round_robin: 'Round robin',
}

export type Tiebreaker = 'headToHead' | 'headToHeadPointDiff' | 'pointDiff' | 'pointsAllowed' | 'pointsFor' | 'setRatio' | 'pointRatio'

export const TIEBREAKER_LABELS: Record<Tiebreaker, string> = {
  headToHead: 'Head-to-head wins',
  headToHeadPointDiff: 'Head-to-head point differential',
  pointDiff: 'Point differential',
  pointsAllowed: 'Fewest points allowed',
  pointsFor: 'Most points scored',
  setRatio: 'Set ratio',
  pointRatio: 'Point ratio',
}

export type BasketballScoring = {
  model: 'basketball'
  periods: 'halves' | 'quarters'
  /** Score recorded for a forfeit win, e.g. 20-0. */
  forfeitScore: number
}

export type VolleyballScoring = {
  model: 'volleyball'
  setsToWin: number
  pointsPerSet: number
  finalSetPoints: number
  winBy: number
  /** Cap on a set's winning score, or null for no cap. */
  pointCap: number | null
}

export type DivisionScoring = BasketballScoring | VolleyballScoring

/**
 * How a group table orders teams before any tiebreaker. 'points': the table points below (Rayan's
 * "3 for a win", the default). 'record': winning percentage, ties counting half, then wins.
 * 'differential': the sport's own differential, point differential in basketball (capped like the
 * PD column) and set differential in volleyball, so a 1-2 team with a big margin can finish above a
 * 2-1 team (T01's "differential first" reading, and volleyball's "sets first").
 */
export type StandingsRankBy = 'points' | 'record' | 'differential'
export const STANDINGS_RANK_BY_LABELS: Record<StandingsRankBy, string> = {
  points: 'Points',
  record: 'Record',
  differential: 'Differential',
}

/** What a double forfeit (neither team showed up) does to the group table. */
export type DoubleForfeitRule = 'bothLose' | 'noResult'
/** Short enough for a two-option switch on a phone; `doubleForfeitSentence` says it in full. */
export const DOUBLE_FORFEIT_LABELS: Record<DoubleForfeitRule, string> = {
  bothLose: 'Both lose',
  noResult: 'No result',
}

/**
 * A division's standings rules (packet T01a, DECISIONS D72 and D72a). Staff set them per division at
 * Divisions, so a rule change needs no new app build. A division stored before these existed has no
 * `standingsRules` field, and every reader goes through `standingsRulesFor`, which supplies
 * `DEFAULT_STANDINGS_RULES` (standings.ts) for the field or any key of it that is missing.
 */
export interface StandingsRules {
  /** See `StandingsRankBy`. */
  rankBy: StandingsRankBy
  /** Table points per result. They rank only with rankBy 'points', which also shows them as a column. */
  winPoints: number
  tiePoints: number
  lossPoints: number
  /** The most one game can add to, or take from, a team's point differential; null for no cap. */
  marginCap: number | null
  /**
   * Whether a forfeit's recorded score counts toward points and differential (basketball: the
   * recorded forfeit score, e.g. 20-0; volleyball: the sets won, at full set points each). When
   * false a forfeit is a win and a loss only.
   */
  forfeitScoreCounts: boolean
  doubleForfeit: DoubleForfeitRule
}

/** Every key of a stored `standingsRules` map, in the order the admin form shows them. */
export const STANDINGS_RULE_KEYS = ['rankBy', 'winPoints', 'tiePoints', 'lossPoints', 'marginCap', 'forfeitScoreCounts', 'doubleForfeit'] as const satisfies readonly (keyof StandingsRules)[]

/**
 * The admin switch for `Division.tiebreakersConfirmed` (D78 Q13 (c)). The Divisions editor and the
 * audit log's value row both read this one label, so a rewording is one edit here. D78's own words,
 * with no "by the sports lead": Kareem confirms them when the sports lead has not (D78 Q13 (d); his
 * answer to wording sheet item 26 (b), 29 Sep 2026, D88).
 */
export const STANDINGS_RULES_CONFIRMED_LABEL = 'Standings rules confirmed'

export interface Division {
  sport: Sport
  /** The key the website uses, e.g. boys_high_competitive */
  key: string
  label: string
  shortLabel: string
  gradeNote: string
  format: DivisionFormat
  cap: number
  sortOrder: number
  active: boolean
  requiresIdPhotos: boolean
  groups: string[]
  tiebreakers: Tiebreaker[]
  /**
   * False until the sports lead confirms the division's standings rules: since D78 this one flag (the
   * admin switch "Standings rules confirmed") covers the points table, forfeit scoring, the cap and
   * the tiebreak order, under its original name. The app and the admin site warn while false.
   */
  tiebreakersConfirmed: boolean
  /**
   * Opt-in automatic seeding (an exception to D79 (C), at Kareem's request, 26 Oct 2026). When true,
   * the finals job fills the division's "Seed N" and "Group X #N" slots from the final standings once
   * every group game is recorded, the standings rules are confirmed and the seeding preview has
   * nothing for staff to decide; it never changes a locked slot, a slot staff filled, or a game that
   * has started. Absent or false: staff fill them with the seeding preview, as before. Only an
   * administrator (event.configure) sets it (firestore.rules); phones never read it.
   */
  autoSeed?: boolean
  /** Manual final order per group, used instead of computed standings when set. */
  standingsOverride: Record<string, string[]> | null
  /** Standings rules; missing on divisions stored before T01a. Read it with `standingsRulesFor`. */
  standingsRules?: StandingsRules
  /** Why, by whom and when each group's final order was set (OV-B2); keyed like `standingsOverride`. */
  standingsOverrideNotes?: Record<string, StandingsStaffNote> | null
  /** Staff adjustments to a team's table points and point differential (OV-B1), keyed by team id. */
  standingsAdjustments?: Record<string, StandingsAdjustment> | null
  /** Staff-pinned places (OV-B2), keyed by team id: that team's place is fixed, the rest computed. */
  standingsPins?: Record<string, StandingsPin> | null
  scoring: DivisionScoring
  slotMinutes: number
  /** Latest sensible start (HH:mm), e.g. "20:00" for young divisions. Publish warns after it. */
  latestStart: string | null
  /** What the teams are playing for, shown in the app (2025 feedback: "rings confusion"). */
  prizeNote: string
  /** Hide scores in the app (for showcase or K-5 divisions if the organizers want). */
  hideScores: boolean
  /** Fields staff edited by hand; imports leave them alone. */
  lockedFields?: string[]
  /**
   * Lane A x B (En2-1): per standings table, then per game id, the games a Schedule publish moved to
   * another division after their recorded result had left this table: the table's stamp from the game
   * (`Game.tableStamps`, when the table last changed for it) and its game number. This division's
   * Brackets page no longer loads those games, so it reads their stamps here and still flags a staff
   * order or pin saved before one. Written only by `schedulePublish`, on that rare path; staff cannot
   * write it (firestore.rules) and the Administrator's editor keeps it as stored. Not named
   * `standings…` (M3): it changes no standings, and phones never read it.
   */
  tableDepartures?: Record<string, Record<string, TableDeparture>> | null
  updatedAt: TimestampLike | null
}

/** One entry of `Division.tableDepartures`. */
export interface TableDeparture {
  /** The game's stamp for the table (`Game.tableStamps`): when its recorded result left the table. */
  at: TimestampLike | null
  gameNumber: number | null
}

/** Key in `standingsOverride` for a division without groups. */
export const NO_GROUP_KEY = 'all'

// ---------------------------------------------------------------- standings overrides (Lane B: OV-B1, OV-B2)
//
// Staff overrides on a division's standings, stored on the division document the app already reads
// (so they add no read). Kareem, 26 Sep: "there should be manual controls on everything to override."
// Written by staff with schedule.edit or event.configure, validated by firestore.rules, read through
// `standingsAdjustmentsFor` / `standingsPinsFor` (standings.ts), and applied by `computeStandings` on
// every read, so nothing automatic recomputes them away.
//
// FROZEN AT THE FIRST STORE BUILD (R11), like `standingsRules` (D72a): the entry key sets below and
// the bounds in standings.ts. An installed binary shows "needs a newer app" and hides places for an
// entry with a key or value it does not know (`unknownStandingsOverrides`), so adding a key or
// widening a bound after R11 needs every family to update the app.
//
// A NEW DIVISION FIELD THAT CHANGES STANDINGS is named `standings…` (M3; KNOWN_STANDINGS_FIELDS in
// standings.ts). From R11 an installed binary shows "needs a newer app" for a `standings…` field it
// does not know, unless the field is null or empty, instead of silently leaving it out.

/** Who set a staff override, when, and why. `by` is the staff uid; `at` is the server time. */
export interface StandingsStaffNote {
  reason: string
  by: string
  at: TimestampLike | null
}

/**
 * A signed adjustment to one team's standings (a penalty or a bonus). Both numbers are whole and
 * signed; at least one is not zero. They are added to the team's computed table points and point
 * differential before ranking, so they count in every ranking mode that uses those numbers.
 */
export interface StandingsAdjustment extends StandingsStaffNote {
  points: number
  pointDiff: number
}

/** One team's place fixed by hand in one table (`group` is the `standingsOverride` key). */
export interface StandingsPin extends StandingsStaffNote {
  group: string
  place: number
}

/**
 * Every key of a stored adjustment or pin: what this build can read. Frozen at R11 (see above). A
 * final-order note has reason, by and at; phones never read it, so it freezes nothing.
 */
export const STANDINGS_ADJUSTMENT_KEYS = ['points', 'pointDiff', 'reason', 'by', 'at'] as const satisfies readonly (keyof StandingsAdjustment)[]
export const STANDINGS_PIN_KEYS = ['group', 'place', 'reason', 'by', 'at'] as const satisfies readonly (keyof StandingsPin)[]

// ---------------------------------------------------------------- teams

export type ProfileStatus = 'none' | 'link_sent' | 'submitted' | 'approved' | 'changes_requested' | 'needs_review'

export interface RosterEntry {
  number: string
  firstName: string
}

/**
 * events/{e}/teams/{teamId}. Public once `published` (listed). `published`, `roster`, `photoUrl`,
 * `homeTown`, `about` and `profileStatus` are written only by Cloud Functions.
 */
export interface Team {
  name: string
  divisionId: string
  sport: Sport
  organization: string
  city: string
  homeTown: string
  about: string
  photoUrl: string
  roster: RosterEntry[]
  profileStatus: ProfileStatus
  group: string | null
  seed: number | null
  published: boolean
  sample?: boolean
  updatedAt: TimestampLike | null
}

export interface PrivatePlayer {
  /** Stable key: the source subdocument id, or a hash of source team id + normalized name. Never an index. */
  key: string
  firstName: string
  lastName: string
  number: string
  /** Shown in the app. Set on approval: coach did not hide the player and staff did not hide them. */
  showInApp: boolean
  /** A parent asked staff to hide this player. Overrides the coach and any re-import. */
  staffHidden: boolean
  /** True when the player appeared in the latest import. */
  inSource: boolean
}

/** events/{e}/teamsPrivate/{teamId}: registrations.view or teams.manage. */
export interface TeamPrivate {
  source: SourceRef | null
  sourceStatus: SourceStatus
  registrationStatus: string
  orgLeader: Contact | null
  headCoach: Contact | null
  assistantCoach: Contact | null
  players: PrivatePlayer[]
  consents: { photoRelease: boolean | null; rulesAccepted: boolean | null }
  canPlayFridayEvening: boolean | null
  coachLink: { sentTo: string | null; issuedAt: TimestampLike | null; expiresAt: TimestampLike | null; emailed: boolean; revokedAt?: TimestampLike | null } | null
  /**
   * Copy of the coach's latest submission, written by a function so staff never need the link token.
   * The photo (when hasPhoto) is copied to STORAGE.reviewPhoto for staff to preview.
   */
  submission?: {
    homeTown: string
    about: string
    players: { key: string; number: string; hide: boolean }[]
    submitterName: string
    hasPhoto: boolean
    photoPreviewPath: string | null
    submittedAt: TimestampLike | null
  } | null
  /** Last review decision on the coach's submission. */
  profileReview?: { decision: 'approved' | 'changes_requested' | 'updated'; note: string; at: TimestampLike | null; byName: string } | null
  /**
   * The last team photo published without a photo release on file: which problem staff overrode (declined,
   * not collected, not recorded), with which photo, who and when. Functions only (F1-216, F1-393; W8 #3).
   */
  photoReleaseOverride?: {
    release: 'declined' | 'not_collected' | 'not_recorded'
    photo: 'coach' | 'staff'
    at: TimestampLike | null
    byUid: string
    byName: string
  } | null
  /** Plain operational notes only. The UI warns against medical details. Max 500 characters. */
  staffNotes: string
  /** Fields staff edited by hand; imports leave them alone (except removal, which always unpublishes). */
  lockedFields: string[]
  updatedAt: TimestampLike | null
}

/** profileLinks/{token}: readable by the token holder while valid. Written only by Cloud Functions. */
export interface ProfileLink {
  eventId: string
  teamId: string
  teamName: string
  divisionLabel: string
  players: { key: string; firstName: string }[]
  expiresAt: TimestampLike
  createdAt: TimestampLike | null
  /** Set when staff approve the submission; locks the link. */
  approvedAt: TimestampLike | null
  revokedAt: TimestampLike | null
  /**
   * When the link document itself is deleted (Firestore TTL). Later than expiresAt, so staff can
   * still review a submission after the coach's window closes.
   */
  deleteAt?: TimestampLike | null
  /** Shown to the coach when staff ask for changes. */
  reviewNote?: string
}

/** profileLinkIndex/{eventId}_{teamId}: functions only. The team's current coach link. */
export interface ProfileLinkIndex {
  eventId: string
  teamId: string
  token: string
  updatedAt: TimestampLike | null
}

/** events/{e}/teamProfileSubmissions/{token}: written by the token holder. */
export interface TeamProfileSubmission {
  homeTown: string
  about: string
  players: { key: string; number: string; hide: boolean }[]
  submitterName: string
  hasPhoto: boolean
  submittedAt: TimestampLike | null
}

// ---------------------------------------------------------------- payments

/** events/{e}/payments/{kind}_{refId}: payments.view only. Mirrors the website, written by the import. */
export interface PaymentDoc {
  kind: 'team' | 'vendor' | 'sponsor'
  refId: string
  status: string
  amount: number | null
  method: string
  paidAt: string | null
  updatedAt: TimestampLike | null
}

// ---------------------------------------------------------------- games

export type GameStatus = 'scheduled' | 'live' | 'final' | 'cancelled' | 'postponed' | 'forfeit'
export type GameStage = 'group' | 'bracket' | 'showcase' | 'placement'
export const GAME_STATUS_LABELS: Record<GameStatus, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  final: 'Final',
  cancelled: 'Cancelled',
  postponed: 'Postponed',
  forfeit: 'Forfeit',
}
export const GAME_STAGE_LABELS: Record<GameStage, string> = {
  group: 'Group stage',
  bracket: 'Bracket',
  showcase: 'Showcase',
  placement: 'Placement',
}

export type SlotSource =
  | { type: 'game'; gameId: string; outcome: 'winner' | 'loser' }
  | { type: 'groupPlace'; group: string; place: number }
  | { type: 'seed'; seed: number }

export interface GameSlot {
  teamId: string | null
  /** Team name copied from the team doc so the app never needs an extra read. */
  name: string | null
  /** Shown while teamId is null, e.g. "Winner of Game 12" or "Group A #1". */
  placeholder: string | null
  source: SlotSource | null
  /**
   * Set by bracket advancement or seeding. 'seeding' is staff's Fill from the seeding preview;
   * 'autoSeed' is the finals job's automatic seeding (`Division.autoSeed`), which it may redo or undo.
   */
  filledFrom: { at: TimestampLike | null; by: 'advance' | 'seeding' | 'autoSeed' | 'manual' } | null
  /** Staff set this slot by hand; automatic advancement leaves it alone. */
  locked: boolean
}

export interface Score {
  home: number
  away: number
}

export interface Period {
  index: number
  label: string
}

/** Winners bracket, elimination bracket, or the final. A seventh fixed vocabulary (plan §5 rule 7). */
export type BracketSide = 'W' | 'L' | 'F'
export const BRACKET_SIDE_LABELS: Record<BracketSide, string> = {
  W: 'winners bracket',
  L: 'elimination bracket',
  F: 'final',
}

export interface BracketPosition {
  side: BracketSide
  round: number
  slot: number
  /** Second final in double elimination, cancelled automatically if not needed. */
  ifNecessary: boolean
}

/** Fields owned by the schedule (drafts and publish). */
export interface GameScheduleFields {
  sport: Sport
  divisionId: string
  stage: GameStage
  group: string | null
  round: string
  bracket: BracketPosition | null
  gameNumber: number | null
  home: GameSlot
  away: GameSlot
  startsAt: TimestampLike
  courtId: string
  durationMinutes: number
  /** Through the schedule only scheduled, postponed or cancelled are set. */
  status: GameStatus
  notes: string
}

export interface GameResult extends GameCorrectionFields {
  score: Score
  winnerSide: Side | null
  /** Volleyball set scores in order. */
  sets: Score[]
  decidedBy: 'final' | 'forfeit' | 'double_forfeit'
  at: TimestampLike | null
}

/** events/{e}/games/{gameId}. Public. Written only by Cloud Functions. */
export interface Game extends GameScheduleFields, GameOverrideFields {
  result: GameResult | null
  /** Expected start (ms) the last reminder announced, so a moved start can be re-announced. */
  reminderFor: number | null
  updatedAt: TimestampLike | null
  sample?: boolean
}

// ---------------------------------------------------------------- manual overrides on a game (Lane A)
// Function-written fields for the staff overrides of audits/2026-09-24/OVERRIDES-AUDIT.md (OV-A*).
// All optional: documents written before them have none, and none of them means "no override".

export interface GameOverrideFields {
  /**
   * Double elimination "if necessary" final only (OV-A4): who last decided whether it is played.
   * 'auto' when the finals job cancelled it because the winners-bracket team won the first final;
   * 'staff' when staff scheduled or cancelled it by hand, which the finals job never overrides.
   */
  ifNecessaryBy?: 'auto' | 'staff' | null
  /**
   * OV-A6: staff hold this game's result for a protest. While it is set the result is "under review":
   * the finals job neither records the result nor advances the bracket nor sends the final-score push,
   * and a result recorded before the hold stays exactly as recorded, status and teams included (a Fix
   * score, forfeit, reopen or desk final made during the hold is applied when it is released; Correct
   * game is refused on a recorded result until the release, and on a result never recorded it changes
   * the teams at once while the next games wait for the release). Releasing it, or a restart of the
   * game (Return to scheduled, an un-cancel, a reset final scheduled again), or a cancel, clears it.
   * The reason is in the audit log, never here (this document is public).
   */
  resultHold?: GameResultHold | null
  /**
   * Lane A x B (B3, En1-1, En2-1): per standings table, keyed by division id and then by table (a
   * `standingsOverride` key: the group, or NO_GROUP_KEY), when a server write last changed what that
   * table counts from this game: its recorded result leaving it (a reset, reopen, cancel or forfeit that
   * clears the result, or a hold's release that undoes it after a reopen during the hold), or Correct
   * game giving a counted result new teams, or moving it into or out of the table. A table keeps its
   * stamp when the game moves on (Correct game, or a Schedule publish), so the Brackets page still flags
   * a staff final order or pin saved there before it (Lane B staleness), and never flags the table the
   * game moved to for a change made before the move. The division key keeps a Group A stamp from being
   * read as another division's Group A after a publish moves the game there (En2-1); that publish also
   * copies the old division's stamps onto its document (`Division.tableDepartures`), because its
   * Brackets page no longer loads the game. A hold, a release that keeps the result, a schedule edit and
   * anything done to a game never recorded leave it alone. Function-written; phones never read it.
   * Read it with `tableStampOf`.
   */
  tableStamps?: Record<string, Record<string, TimestampLike>> | null
  /**
   * Lane A x B (En1-1): when Correct game last moved this game into another table (a group or stage
   * change). The Brackets page ignores this game's scoring-record time up to then in the table it
   * joined: a reopen or a keeper's scoring before the move was no change of that table.
   * Function-written; phones never read it.
   */
  tableJoinedAt?: TimestampLike | null
}

/** A protest hold on a game (OV-A6). Public, function-written. */
export interface GameResultHold {
  /** When the hold was placed. */
  at: TimestampLike | null
  /**
   * The held result, for a held game whose result is not on `games.result` (a result never recorded
   * stays off it until the release). Phones show it as "under review", never as a final: the live
   * summary drops a finished game after three hours, and without this the app would fall back to a
   * 0-0 final. `holdPending` sets it when the hold is placed and the finals job refreshes it while the
   * game is finished: the finished result as it stands, or null when the game is not finished (a hold
   * placed during play) or `games.result` is set. A reopen during the hold leaves the last one, so a
   * game played again can carry a stale `pending` until it finishes again (phones show it as "under
   * review" until the live summary carries the game, Lane D C1). Written whenever `games.result` is
   * empty at those moments, it also carries a change to a recorded result that was waiting for the
   * finals job when the hold was placed (a forfeit with a winner empties `games.result`); `recorded`
   * is then true, and phones show no numbers rather than that change, which is neither recorded nor
   * told (Lane D, `packages/app-core/src/view/game/review.ts`).
   */
  pending?: { status: 'final' | 'forfeit'; score: Score; winnerSide: Side | null; sets: Score[] } | null
  /**
   * Whether the result was recorded (counted in the tables and the bracket) when the hold was placed
   * (`recordedNow`, `resultState.ts`). Frozen for the hold's duration: it decides what the hold keeps
   * exactly as it is, status included, and whether Correct game is refused, so nothing done during
   * the hold (a reopen included) can change either (Lane A n6, Cn6-1). Phones read it too (Lane D,
   * frozen with the first store build, R11): held and recorded, they show `games.result` or no numbers,
   * never `pending`; held and never recorded, they show `pending`.
   */
  recorded?: boolean
}

/**
 * OV-A5 (data for Lane D's "Score corrected by staff"): set when staff changed a result that had
 * already been recorded (the finals job processed it, so phones had it and its push went out). That
 * is a Fix score on such a result, a forfeit over it, a reopen of it, or new teams on it (Correct
 * game). A change before the result is recorded, a keeper's 90-second undo included, is part of
 * getting the final right and is not marked; a ruling on a protest-held result that was never
 * recorded is not marked either. On `gameLive` it survives a reopen and re-final, and `finalsTick`
 * copies it onto `games.result`. A return to scheduled clears it; the replay's result is marked again
 * when it is recorded over a final followers were told (D88 item 9a, `replayCorrects`), as its push
 * says "Corrected final" by default, unless the desk entered exactly that final (both teams, score, set
 * scores compared strictly, forfeit or played: `toldSameFinal`) with its alert switched off
 * (`pushChoice.deskExact`) and it is still exactly that final when it is recorded. Lane E's Reinstate
 * restore of a struck result is that desk entry, and then puts back the mark the struck result had, if
 * any (D92, `WithdrawalGameBefore.correctedAt`).
 * Function-written.
 */
export interface GameCorrectionFields {
  correctedAt?: TimestampLike | null
}

/**
 * OV-A5: when what phones can see of a division's results last changed: a publish, a change while
 * published, or an unpublish. A save of places nobody can see yet leaves it alone. The app's results
 * version marker (liveSummary `versions.results`) is the newest of these, so an unpublish reaches
 * phones within a minute instead of the 6-hour TTL. Function-written.
 */
export interface DivisionResultsVersionFields {
  visibleChangedAt?: TimestampLike | null
}

/**
 * Staff alerts raised by the Lane A overrides. Function-written; staff can only resolve an alert
 * (firestore.rules), and never one with `holdReminder`.
 */
export interface AlertOverrideFields {
  /**
   * Less urgent than the alert type says: 'info' is a notice that needs no action (a reset final
   * cancelled because the winners-bracket team won, as planned); 'check' is worth a look.
   */
  level?: 'info' | 'check' | null
  /**
   * The reminder of a protest hold (OV-A6). It stays open while the hold stands: releasing the hold,
   * or a restart of the game, resolves it. Staff cannot mark it resolved.
   */
  holdReminder?: boolean
}

export type LiveStatus = 'scheduled' | 'live' | 'final' | 'forfeit' | 'cancelled' | 'postponed'

/**
 * events/{e}/gameLive/{gameId}. Public. Scorekeepers update it together with a score event
 * (see firestore.rules). Basketball: `score` is points. Volleyball: `score` is sets won and
 * `setScore` is the set in play.
 */
export interface GameLive extends GameCorrectionFields {
  /** Scoring generation. Missing on legacy games means 0; desk fixes and outcome boundaries advance it. */
  scoringEpoch?: number
  courtId: string
  divisionId: string
  sport: Sport
  status: LiveStatus
  score: Score
  /** Basketball points per period: p1, p2, ... */
  periodScores: Record<string, Score>
  period: Period | null
  /** Volleyball: set in play (n is 1-based). */
  setScore: { n: number; home: number; away: number } | null
  /** Volleyball: finished sets s1, s2, ... */
  setResults: Record<string, Score>
  lastEventId: string | null
  startedAt: TimestampLike | null
  endedAt: TimestampLike | null
  finalizedAt: TimestampLike | null
  /** Computed by Cloud Functions at final. */
  winnerSide: Side | null
  /** finalizedAt (ms) that bracket advancement has processed. */
  advancedFor: number | null
  /**
   * The final-score push decided for the final the live record carries: true or false, set by a desk
   * forfeit or final, a Fix score or a protest hold's release; null leaves it to the finals job, which
   * then takes the default from `told` (`finalPushDefault`, `resultState.ts`). A reopen clears it: a
   * choice belongs to the final it was made for. Function-only.
   */
  notifyFinal?: boolean | null
  /**
   * RECORDED (`resultState.ts`): the winner the finals job last recorded, which the tables and the
   * bracket still have, kept while a change to that recorded result waits for the finals job: a Fix
   * score that changes its winner, a forfeit or double forfeit over it, or a protest hold's release
   * that leaves a change for the finals job. `games.result` then shows the change at once (or, for a
   * forfeit with a winner, nothing), so without this a hold, a second Fix score or a forfeit in that
   * minute could not tell that the bracket still has the old winner (Lane A n2 En2-1; n3 Cn3-1,
   * En3-4, En3-5; n4 En4-4, En4-5, Cn4-2). A later change keeps the first value. It says nothing
   * about followers (that is `told`). A reopen outside a hold clears it with the result (the status
   * trigger empties the bracket slots it filled); a reopen during a hold of a recorded result leaves
   * it, as the hold keeps the result. Cleared when the finals job records the result again, when the
   * bracket slots are emptied (a reopen or cancel outside a hold), and by every restart.
   * Function-written; not in `liveSummary`.
   */
  recordedWinner?: { winnerSide: Side | null } | null
  /**
   * TOLD (`resultState.ts`): what followers have on their phones, the last final or corrected final
   * that went out, with the winning side and TEAM (Correct game can put another team on that side, n7
   * En7-1), the score, the set scores (volleyball) and the exact title pushed. Stamped by functions only
   * where that push is queued (the finals job, a Fix score or Correct game that sends it at once), so it
   * means "queued for followers", not "delivered" (n7, En7-9). Nothing else changes it: a reopen, a
   * cancel, a restart and a replay all leave followers with what they had (n8). It decides the
   * final-score push default (on when the winning team differs from it, off while that team stands,
   * whatever the score, the first final's default when there is none, on for the replay after a restart:
   * `restarted`) and whether the push says "Corrected final" (Lane A n6). A new live record starts with
   * null (`initialLive`). A record with no
   * field at all predates it: functions read it from the game's `final_score` announcements at their
   * first write to it (n8), and until then the desk treats it as unsure.
   * Function-written: every client update of `gameLive` is a field allow-list that never names it. Not
   * in `liveSummary`, and phones never read it (R11).
   */
  told?: GameTold | null
  /**
   * Staff's last explicit final-score push choice for a result the finals job has not recorded yet: the
   * `notify` sent with a Fix score that leaves the push to the finals job, a desk forfeit or final, or a
   * protest hold's release, with the final it was made for (`finalizedAt`, ms) and the winner it pushes
   * (side and team, n7). It is the push default while that final stands and, when followers were told a
   * final (`told`), while its winning team stands (`chosenPush`, `resultState.ts`); otherwise the push default follows
   * `told`. A double forfeit has none. Cleared when the finals job records the result, by a reopen
   * (at the desk, or from a console: the status trigger) and by every restart (Lane A n4, which
   * replaced n3's `resultHold.quiet`; n6). Function-written (`gameLive` updates by clients are field
   * allow-lists that never name it); not in `liveSummary`, and phones never read it (R11).
   */
  pushChoice?: {
    notify: boolean
    winnerSide: Side | null
    winnerTeamId?: string | null
    finalizedAt: number
    /**
     * Set (true) only by the desk (`gameSetOutcome`, a final or forfeit, which Lane E's Reinstate
     * restore also calls) for an "off" chosen for the replay after a restart when the result entered is
     * exactly the final followers have (`toldSameFinal`: the same winning and losing teams and score,
     * the same set scores compared strictly, so none never equals some, and the same kind of result,
     * forfeit or played): the one replay that is not marked corrected (D88 item 9a n3, n4,
     * `replayCorrects`), if it is still exactly that final when it is recorded. Any other writer of the
     * choice (Fix score, Correct game, a release) replaces it without the flag (Va2-2).
     */
    deskExact?: boolean
  } | null
  /**
   * The game was restarted (Return to scheduled, a reset final scheduled again, an un-cancel publish:
   * `notStartedFields`) and the replay's final is not recorded yet (Lane A n8.1). `told` is then a
   * final for play that was undone, so the replay's final-score alert defaults on and goes out as
   * "Corrected final" (Kareem, 28 Sep, #25); staff can switch it off. Cleared when the replay's push
   * goes out (the finals job, or a Fix score or Correct game that sends at once) or when the finals job
   * records it with staff's explicit "off" (n8.2 Cn10-2, n8.3 V11-1); after that a Fix score follows the
   * usual rule. A replay recorded with nothing sent (alerts off, a double forfeit) keeps it. Missing
   * means false. Function-written (`gameLive` updates by clients are field allow-lists that never name
   * it); not in `liveSummary`, and phones never read it (R11).
   */
  restarted?: boolean | null
  updatedAt: TimestampLike | null
}

/** A final-score push that went out (`gameLive.told`; Lane A n6, n7, n8). */
export interface GameTold {
  winnerSide: Side | null
  /** The team that won in that push; null for no winner. Missing only on a stamp from before n7. */
  winnerTeamId?: string | null
  /**
   * The team that lost in that push; null for no winner, or no team on that side (D88 item 9a n3,
   * ANSWERS-APP-VERIFY-n2 Va2-1). Missing on a stamp from before it and on one read back from an
   * announcement: such a final is then never "the very same final" for the replay's mark.
   */
  loserTeamId?: string | null
  /**
   * Whether that push was for a forfeit (true) or a played final (false) (D88 item 9a n4,
   * ANSWERS-APP-VERIFY-n3 Va3-1). Missing on a stamp from before it and on one read back from an
   * announcement: such a final is then never "the very same final" for the replay's mark.
   */
  forfeit?: boolean
  score: Score
  /** Volleyball: the set scores in that push. */
  sets?: Score[]
  title: string | null
  at: TimestampLike | null
  /**
   * Read back from an announcement whose title did not give the winner and score (n8): followers have
   * `title`, but the desk cannot tell whether a result changes its winner. Absent on a stamp.
   */
  unsure?: boolean
}

export type ScoreEventType = 'start' | 'points' | 'period' | 'endset' | 'final' | 'reopen' | 'undo' | 'correction'

/**
 * events/{e}/gameLive/{gameId}/events/{eventId}. Staff only, create-only.
 * Ids: start/points/final/reopen `${uid}_${16 digits}`, undo `undo_${targetId}`, period `period_${fromIndex}`,
 * endset `endset_${n}`; functions write corrections as `corr_${uid}_${ms}` (scoreSetAbsolute) and
 * `outcome_${outcome}_${uid}_${ms}` (gameSetOutcome).
 * Events are never edited, but a correction that takes back a period or a set deletes the matching
 * `period_*` / `endset_*` events in the same transaction, so the console can end them again. Readers
 * must not assume a fixed-id event, once seen, stays.
 */
export interface ScoreEvent {
  /** Generation observed by the keeper when this action was queued. Legacy events mean 0. */
  scoringEpoch?: number
  type: ScoreEventType
  side: Side | null
  delta: number
  /** Basketball period index or volleyball set number the points belong to. */
  segment: number | null
  /** For undo: the event being reversed. */
  target: string | null
  courtId: string
  by: string
  byName: string
  at: TimestampLike | null
  note: string
}

export type DraftOp = 'create' | 'update' | 'cancel'

/** A slot as staff set it in a draft; the server fills in `name` and `filledFrom`. */
export type DraftSlot = Pick<GameSlot, 'teamId' | 'placeholder' | 'source' | 'locked'>

export type DraftChangesDoc = Partial<Omit<GameScheduleFields, 'home' | 'away'>> & { home?: DraftSlot; away?: DraftSlot }

/** events/{e}/gameDrafts/{gameId}: schedule.edit. */
export interface GameDraft {
  op: DraftOp
  /** For op=create the full schedule; for update only the changed fields. */
  changes: DraftChangesDoc
  /** games/{id}.updatedAt when the draft was started; publish rejects the draft if the game changed since. */
  baseUpdatedAt: TimestampLike | null
  editedBy: string
  editedByName: string
  editedAt: TimestampLike | null
  note: string
}

export interface ScheduleChange {
  gameId: string
  op: DraftOp
  summary: string
  fields: string[]
  publishId: string
  publishedAt: TimestampLike | null
  publishedBy: string
  notifiedTeamIds: string[]
  /** This game's old → new schedule values, in words (Lane C). Absent on changes published before it. */
  changes?: AuditChange[]
}

/** events/{e}/courtStatus/{courtId}: public. */
export interface CourtStatus {
  delayMinutes: number
  /** Delay applies to scheduled games on this court starting at or after this time, same day. */
  since: TimestampLike | null
  note: string
  scoringMode: 'running' | 'finalOnly'
  /** Delay last announced to followers, for the change threshold. */
  announcedDelay: number
  /** Event-zone day (YYYY-MM-DD) announcedDelay belongs to; a new day starts from 0. Function-only. */
  announcedFor?: string | null
  updatedAt: TimestampLike | null
  updatedBy: string | null
}

/** events/{e}/courtKeepers/{courtId}: staff only. */
export interface CourtKeepers {
  keeperUids: string[]
  keepers: Record<string, { name: string; seenAt: TimestampLike | null }>
  updatedAt: TimestampLike | null
}

/** `job_failed`: a scheduled job (finals, the live summary) keeps failing; functions/src/lib/jobAlerts.ts (F1-229, F1-311). */
export type AlertType = 'bracket_conflict' | 'score_drift' | 'push_failed' | 'seeding_ready' | 'import_review' | 'double_forfeit' | 'job_failed'

/** events/{e}/alerts/{id}: staff only, written by functions, resolved by staff. */
export interface AlertDoc extends AlertOverrideFields {
  type: AlertType
  title: string
  detail: string
  gameId: string | null
  divisionId: string | null
  refId: string | null
  createdAt: TimestampLike | null
  resolved: boolean
  resolvedBy: string | null
  resolvedAt: TimestampLike | null
}

/** events/{e}/results/{divisionId}: public when published. */
export interface DivisionResults extends DivisionResultsVersionFields {
  divisionId: string
  places: { place: number; teamId: string; teamName: string }[]
  published: boolean
  publishedAt: TimestampLike | null
}

// ---------------------------------------------------------------- announcements

export type AudienceType = 'all' | 'sections' | 'divisions' | 'teams' | 'courts'

export interface Audience {
  type: AudienceType
  /**
   * For `sections`, the target sections. For every other type, the sections the audience touches
   * (the rules compare this with the sender's scope and the dispatcher checks it against the real data).
   */
  sections: SectionKey[]
  divisionIds: string[]
  teamIds: string[]
  courtIds: string[]
}

export type AnnouncementStatus = 'scheduled' | 'queued' | 'sending' | 'sent' | 'failed' | 'cancelled'
/**
 * `promotion` (D158, Kareem 24 Oct 2026, reversing D152 (3)): the one push a live Premium vendor
 * promotion sends when it goes live, to the phones with the bazaar section on. Automatic only, never in
 * the feed or in Your alerts (functions/src/lib/promotionPush.ts).
 */
export type AnnouncementKind = 'manual' | 'schedule_change' | 'schedule_posted' | 'final_score' | 'game_reminder' | 'court_delay' | 'results' | 'promotion'

/** events/{e}/announcements/{id}: staff only. */
export interface Announcement {
  /** Fixed push expiry in epoch ms. A retry cannot reset this clock. */
  pushExpiresAt?: number
  /** Expected tip-off used to compose this reminder; changed schedules obsolete it. */
  reminderFor?: number
  title: string
  body: string
  audience: Audience
  sendAt: TimestampLike | null
  status: AnnouncementStatus
  /** Show in the app's announcements feed (a public copy goes to feed/{id}). */
  feed: boolean
  push: boolean
  urgent: boolean
  kind: AnnouncementKind
  automatic: boolean
  createdBy: string
  createdByName: string
  createdAt: TimestampLike | null
  sentAt: TimestampLike | null
  /**
   * `sent` and `failed` are each phone's final answer from FCM, after the dispatcher's one retry.
   * `retried` and `recovered` are written only when a retry ran: how many phones were tried again, and
   * how many of those FCM then accepted (already counted in `sent`).
   */
  reach: { targeted: number; sent: number; failed: number; retried?: number; recovered?: number } | null
  dispatch: { id: string; leaseUntil: TimestampLike | null; attempts: number; cursor: string | null } | null
  dryRun: boolean
  error: string | null
  relatedGameId: string | null
  cancelledBy: string | null
  cancelledAt: TimestampLike | null
  /** Push tag; notifications with the same tag replace each other. Defaults to the announcement id. Functions only. */
  tag?: string | null
  /**
   * `kind: 'promotion'` only: which promotion this push is about, by its identity (the vendor and the
   * window's start, `promotionPushId`). The dispatcher re-checks that it is still live and within the
   * Premium cap before sending. Functions only.
   */
  promotion?: { vendorId: string; startsAtMs: number }
}

/** events/{e}/feed/{announcementId}: public copy of a sent announcement. */
export interface FeedItem {
  title: string
  body: string
  kind: AnnouncementKind
  audienceLabel: string
  audienceType: AudienceType
  sections: SectionKey[]
  teamIds: string[]
  urgent: boolean
  sentAt: TimestampLike | null
}

/** Public, sanitized recovery record. Not evidence of delivery to any particular phone. */
export interface AlertHistoryItem extends FeedItem {
  eventId: string
  announcementId: string
  relatedGameId: string | null
  /** all, section:<key>, team:<id>; bounded OR query, never one read per followed team. */
  audienceKeys: string[]
  available: boolean
  withdrawnAt: TimestampLike | null
  /** Push usefulness boundary; old items can remain as labelled historical updates. */
  pushExpiresAt: number
  /** Provisional seven-day retention; requires policy approval before production release. */
  expireAt: TimestampLike
}

/** events/{e}/devices/{fidHash}: Cloud Functions only. */
export interface DeviceDoc {
  fid: string
  teams: string[]
  sections: SectionKey[]
  platform: 'web' | 'ios' | 'android'
  pushCapable: boolean
  createdAt: TimestampLike | null
  updatedAt: TimestampLike | null
  expireAt: TimestampLike | null
  /**
   * The last send FCM refused for this installation, after the retry: FCM's code and Apple's reason only,
   * and how many sends have failed for it. Functions only; registerDevice keeps it, and turning alerts
   * off deletes the doc and with it the record.
   */
  lastPushFailure?: DevicePushFailure
}

export interface DevicePushFailure {
  code: string
  apnsReason: string | null
  at: TimestampLike | null
  announcementId: string
  count: number
}

// ---------------------------------------------------------------- bazaar

export type VendorKind = 'booth' | 'food' | 'refreshment'

/**
 * A paid vendor promotion's tier (D152, reviving D125 (1) for 2026). SPARK staff sell promotions and
 * take payment outside the app; the app only displays them. Premium: the Bazaar's "Featured deals"
 * banner, the once-a-day deal card, the Home deals card and the highlighted tile. Standard: the
 * highlighted tile only.
 */
export type PromotionTier = 'premium' | 'standard'
export const PROMOTION_TIERS: readonly PromotionTier[] = ['premium', 'standard']

/**
 * `Vendor.promotion`. Written only by staff in the admin site (vendors.manage); the website import
 * never writes it (VENDOR_ADMIN_OWNED_FIELDS). The app shows a promotion only while `active` is true
 * and `startsAt <= now < endsAt`; outside that it is as if there were none. No codes, no tracking.
 */
export interface VendorPromotion {
  tier: PromotionTier
  /** The deal in a few words, e.g. "15% off all abayas". 1..PROMOTION_LIMITS.headline characters. */
  headline: string
  /** Optional fine print, e.g. "Not with other offers". '' when none. */
  detail: string
  startsAt: TimestampLike
  endsAt: TimestampLike
  active: boolean
}

/** The promotion and food-court limits, shared by the rules tests, the admin dialog and the app. */
export const PROMOTION_LIMITS = {
  headline: 40,
  detail: 120,
  /** The longest window one promotion may run (endsAt - startsAt). */
  maxWindowDays: 30,
  /** The Premium cap when `config/bazaarPromotions` has none (Kareem, 22 Oct 2026: 4, staff-editable). */
  premiumCapDefault: 4,
  /** The highest cap staff may set, and the most Premium promotions the app ever shows at once. */
  premiumCapCeiling: 8,
  /** A deal ending within this long shows a countdown ("flash deal"). */
  flashWindowMs: 2 * 60 * 60 * 1000,
  /** `Vendor.signatureItems`: at most this many, each at most `signatureItem` characters. */
  signatureItems: 3,
  signatureItem: 40,
} as const

/**
 * Public vendor fields that staff own outright: the website import never writes them, and editing
 * them adds no import lock (they are not in any import mapping).
 */
export const VENDOR_ADMIN_OWNED_FIELDS = ['promotion', 'signatureItems'] as const

/** events/{e}/vendors/{id}: public when published. */
export interface Vendor {
  name: string
  category: string
  description: string
  website: string
  instagram: string
  cityState: string
  logoUrl: string
  booths: string[]
  published: boolean
  featured: boolean
  kind: VendorKind
  sortName: string
  sample?: boolean
  /**
   * A paid promotion (D152), or null/absent for none. Absent on every vendor written before D152;
   * once present, every write keeps the key (null clears it), which the rules enforce so an admin tab
   * opened before D152 cannot silently drop a paid promotion.
   */
  promotion?: VendorPromotion | null
  /** Food court only, optional: up to three signature dishes or drinks (D152). Staff-owned. */
  signatureItems?: string[]
  updatedAt: TimestampLike | null
}

/** events/{e}/vendorsPrivate/{id}: vendors.manage. No payment data (see payments). */
export interface VendorPrivate {
  source: SourceRef | null
  sourceStatus: SourceStatus
  contactPerson: string
  email: string
  phone: string
  boothsRequested: { boothId: string; boothType: string }[]
  needsElectricity: boolean
  electricityNotes: string
  extraTables: number
  extraChairs: number
  specialRequirements: string
  staffNotes: string
  lockedFields: string[]
  updatedAt: TimestampLike | null
}

export interface BazaarBooth {
  id: string
  type: string
  block: string
  row: number
  col: number
}

export interface BazaarBlock {
  id: string
  label: string
  frame: { x: number; y: number; cellWidth: number; cellHeight: number }
  /** Rows of booth ids (null = empty cell). Firestore cannot store nested arrays, so each row is {cells}. */
  grid: { cells: (string | null)[] }[]
}

export interface BazaarArea {
  id: string
  kind: string
  label: string
  x: number
  y: number
  width: number
  height: number
  vertical?: boolean
}

export interface BazaarLayout {
  blocks: BazaarBlock[]
  booths: BazaarBooth[]
  map: {
    viewBox: { x: number; y: number; width: number; height: number }
    hall: { x: number; y: number; width: number; height: number }
    areas: BazaarArea[]
    aisles?: { id?: string; x: number; y: number; width: number; height: number }[]
    exits?: { id?: string; label?: string; x: number; y: number; width?: number; height?: number; side?: string; main?: boolean }[]
    [key: string]: unknown
  }
  pricing?: unknown
  maxBooths?: number
  promoDiscount?: number
  updatedAt?: TimestampLike | null
}

/** events/{e}/config/sponsorTiers */
export interface SponsorTiersConfig {
  tiers: SponsorTier[]
  updatedAt: TimestampLike | null
}

/**
 * events/{e}/config/bazaarPromotions: staff settings for paid promotions (D152). Read by the admin site
 * only; the attendee app never reads it (it caps Premium at PROMOTION_LIMITS.premiumCapCeiling).
 */
export interface BazaarPromotionsConfig {
  /** How many Premium promotions may be on at once, 1..PROMOTION_LIMITS.premiumCapCeiling. */
  premiumCap: number
  updatedAt: TimestampLike | null
}

/** events/{e}/config/vendorCategories */
export interface VendorCategoriesConfig {
  labels: Record<string, string>
  updatedAt: TimestampLike | null
}

// ---------------------------------------------------------------- sponsors

export type SponsorPlacement = 'splash' | 'home_partners' | 'schedule_header' | 'bracket_presented_by' | 'announcement_footer' | 'bazaar'
export const SPONSOR_PLACEMENTS: readonly SponsorPlacement[] = ['splash', 'home_partners', 'schedule_header', 'bracket_presented_by', 'announcement_footer', 'bazaar']
export const SPONSOR_PLACEMENT_LABELS: Record<SponsorPlacement, string> = {
  splash: 'Splash screen',
  home_partners: 'Home screen partners',
  schedule_header: 'Schedule header',
  bracket_presented_by: 'Brackets "presented by"',
  announcement_footer: 'Announcement footer',
  bazaar: 'Bazaar page',
}

/**
 * The website's sponsor tier slugs, highest first. `Sponsor.tier` is that slug (the import lower-cases
 * it, and the admin site's tier picker stores it). The attendee app ranks the Home partner tiles by it
 * (`homePartners`, packages/app-core) so the order costs no read of `config/sponsorTiers`; a tier this
 * list does not name ranks after every listed one. functions/src/imports.ts keeps the same order for
 * the default `sortOrder` it gives an imported sponsor (`tierSortOrder`).
 */
export const SPONSOR_TIER_ORDER = ['presenting', 'title', 'platinum', 'gold', 'silver', 'bronze', 'supporter'] as const

/** One of the website's tier slugs, `SPONSOR_TIER_ORDER`. */
export type SponsorTierSlug = (typeof SPONSOR_TIER_ORDER)[number]

/**
 * A short display word for each tier slug, for the attendee app's Home tile badge (Kareem, 21 Oct
 * 2026, extends D149). Static, beside the order, so a badge costs no read of `config/sponsorTiers`.
 * That config's `displayName` stays the admin site's; the badge says only the short word.
 */
export const SPONSOR_TIER_LABELS: Readonly<Record<SponsorTierSlug, string>> = {
  presenting: 'Presenting',
  title: 'Title',
  platinum: 'Platinum',
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  supporter: 'Supporter',
}

/** The tier slug `tier` names (trimmed, any case), or null for a tier `SPONSOR_TIER_ORDER` does not list. */
export function sponsorTierSlug(tier: string | null | undefined): SponsorTierSlug | null {
  const key = typeof tier === 'string' ? tier.trim().toLowerCase() : ''
  return (SPONSOR_TIER_ORDER as readonly string[]).includes(key) ? (key as SponsorTierSlug) : null
}

/** A tier's place in `SPONSOR_TIER_ORDER` (0 is the top), or the list's length for any other tier. */
export function sponsorTierRank(tier: string | null | undefined): number {
  const key = typeof tier === 'string' ? tier.trim().toLowerCase() : ''
  const i = (SPONSOR_TIER_ORDER as readonly string[]).indexOf(key)
  return i === -1 ? SPONSOR_TIER_ORDER.length : i
}

/** events/{e}/sponsors/{id}: public when published. */
export interface Sponsor {
  name: string
  tier: string
  logoUrl: string
  website: string
  placements: SponsorPlacement[]
  published: boolean
  sortOrder: number
  sample?: boolean
  updatedAt: TimestampLike | null
}

/** events/{e}/sponsorsPrivate/{id}: sponsors.manage. */
export interface SponsorPrivate {
  source: SourceRef | null
  sourceStatus: SourceStatus
  contactPerson: string
  email: string
  phone: string
  staffNotes: string
  lockedFields: string[]
  updatedAt: TimestampLike | null
}

export interface SponsorTier {
  slug: string
  displayName: string
  amount: number | null
  active: boolean
  placements: SponsorPlacement[]
}

// ---------------------------------------------------------------- quran

export type QuranGroup = 'A' | 'B'
export const QURAN_GROUPS: readonly QuranGroup[] = ['A', 'B']
export const QURAN_GROUP_LABELS: Record<QuranGroup, string> = {
  A: 'Group A · Grades 1 to 6',
  B: 'Group B · Grades 7 to 12',
}
export type QuranEntrantStatus = 'waiting' | 'now' | 'done' | 'absent'

/** events/{e}/quranEntrants/{id}: quran.manage only. */
export interface QuranEntrant {
  firstName: string
  lastName: string
  grade: number | null
  gender: string
  group: QuranGroup
  organization: string
  orgType: string
  /** Fractional ordering key; moving one entrant changes only its own key. */
  sortKey: number
  status: QuranEntrantStatus
  checkedIn: boolean
  passage: string
  registrationStatus: string
  source: SourceRef | null
  sourceStatus: SourceStatus
  /** Fields staff edited by hand; imports leave them alone. */
  lockedFields?: string[]
  sample?: boolean
  updatedAt: TimestampLike | null
}

/**
 * events/{e}/quranOrder/{id} (same id as the entrant): public. Never a first or last name; only
 * `displayName`, which Cloud Functions alone write in the live name format ('' under org_only).
 */
export interface QuranOrderEntry {
  group: QuranGroup
  sortKey: number
  organization: string
  status: QuranEntrantStatus
  passage: string
  /** Written only by Cloud Functions (quranSetNameFormat, the draw, imports, the entrant trigger). Missing = ''. */
  displayName?: string
  updatedAt: TimestampLike | null
}

/** events/{e}/quranLive/{group}: public. */
export interface QuranLive {
  group: QuranGroup
  currentEntrantId: string | null
  /** Increments on every advance; the rules require exactly +1. */
  step: number
  paused: boolean
  message: string
  /**
   * How the app names students on the running order and the now-reciting card, chosen by staff with
   * quranSetNameFormat and the same on both groups. Missing = 'org_only'. Clients cannot change it.
   */
  nameFormat?: QuranNameFormat
  updatedAt: TimestampLike | null
  updatedBy: string | null
}

export type QuranNameFormat = 'org_only' | 'first_name' | 'first_last_initial'

/** events/{e}/quranResults/{group}: public when published. Written only by Cloud Functions. */
export interface QuranResults {
  group: QuranGroup
  nameFormat: QuranNameFormat
  places: { place: number; displayName: string; organization: string }[]
  published: boolean
  publishedAt: TimestampLike | null
}

// ---------------------------------------------------------------- check-in

export type CheckinStatus = 'pending' | 'partial' | 'checked_in'

/** events/{e}/checkins/{team_<id>|vendor_<id>}: checkin.manage. `facts` are written by the import. */
export interface Checkin {
  kind: 'team' | 'vendor'
  refId: string
  status: CheckinStatus
  items: Record<string, boolean>
  facts: {
    paymentOk: boolean | null
    requiresIdPhotos: boolean
    rosterCount: number
    waiverOnFile: boolean | null
    importedAt: TimestampLike | null
  }
  checkedInAt: TimestampLike | null
  by: string | null
  byName: string | null
  notes: string
  updatedAt: TimestampLike | null
}

export const TEAM_CHECKIN_ITEMS = [
  { key: 'rosterConfirmed', label: 'Roster confirmed with the coach', onlyIfIds: false },
  { key: 'waiversCollected', label: 'Consent and waiver forms collected', onlyIfIds: false },
  { key: 'idsChecked', label: 'Player IDs checked on sparkmidwest.org', onlyIfIds: true },
  { key: 'paymentConfirmed', label: 'Payment confirmed', onlyIfIds: false },
] as const

export const VENDOR_CHECKIN_ITEMS = [
  { key: 'arrived', label: 'Arrived for load-in' },
  { key: 'paymentConfirmed', label: 'Payment confirmed' },
  { key: 'boothReady', label: 'Booth set up' },
] as const

// ---------------------------------------------------------------- email

export type EmailAudience = 'vendors_all' | 'vendors_electricity' | 'food_vendors' | 'coaches_all'
export const EMAIL_AUDIENCE_LABELS: Record<EmailAudience, string> = {
  vendors_all: 'All bazaar vendors',
  vendors_electricity: 'Vendors who asked for electricity',
  food_vendors: 'Food and refreshment vendors',
  coaches_all: 'All head coaches',
}
export type EmailCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled'

/** events/{e}/emailCampaigns/{id}: vendors.manage (coach audiences also need teams.manage). */
export interface EmailCampaign {
  name: string
  audience: EmailAudience
  subject: string
  /** Plain text with merge fields: {{name}}, {{contact}}, {{booths}}, {{team}}, {{division}}, {{event}} */
  body: string
  sendAt: TimestampLike | null
  status: EmailCampaignStatus
  sentCount: number
  createdBy: string
  createdByName: string
  createdAt: TimestampLike | null
  sentAt: TimestampLike | null
  error: string | null
  updatedAt: TimestampLike | null
}

export const EMAIL_MERGE_FIELDS = ['name', 'contact', 'booths', 'team', 'division', 'event'] as const

// ---------------------------------------------------------------- audit and imports

/**
 * One value an audit entry records as changed (Lane C, G12): public, allow-listed values only, in the
 * admin site's words. Never personal data (functions/src/lib/auditValues.ts holds the allow-list).
 */
export interface AuditChange {
  /** Stable key: a field path ("standingsRules.lossPoints", "tiebreakers") or "<gameId>" / "<gameId>.home". */
  key: string
  /** What changed, in the admin site's words: "Points for a loss", "Game 12 · Eagles vs Hawks". At most 120 characters. */
  label: string
  /** Old and new value in words; "none" for no value. At most 160 characters each. */
  from: string
  to: string
}

/** events/{e}/auditLog/{id}: audit.view. Written only by Cloud Functions. */
export interface AuditEntry {
  at: TimestampLike | null
  uid: string | null
  name: string
  action: string
  target: { type: string; id: string } | null
  summary: string
  /** Names of changed fields only. */
  fields: string[]
  /** Old → new values, for allow-listed public fields and staff actions (Lane C). Absent on older entries. At most 30. */
  changes?: AuditChange[]
  /** How many further changes were left out of `changes`. */
  changesMore?: number
  expireAt: TimestampLike | null
}

/** events/{e}/imports/{id}: import.run. */
export interface ImportRecord {
  kind: ImportKind
  source: 'site' | 'upload'
  fileName: string
  at: TimestampLike | null
  by: string
  byName: string
  dryRun: boolean
  counts: { created: number; updated: number; unchanged: number; removed: number; skipped: number }
  /** Record ids and short reasons only. */
  warnings: string[]
  needsReview: { kind: string; id: string; reason: string }[]
  /** Set when the run failed. */
  error?: string | null
}

/** Wraps a document with its id. */
export type WithId<T> = T & { id: string }

// ---------------------------------------------------------------- team withdrawal (Lane E)
// A team that leaves the tournament: the Withdraw wizard on the admin team page (OV-E1, audit G10).
// `teamWithdraw` / `teamReinstate` (functions/src/withdraw.ts) run each game step through Lane A's own
// callables with the staff member's identity, so holds, corrections, the scoring epoch and the
// per-game audit apply unchanged. The record below is staff-only: phones read nothing new.

/** What the withdrawal does to one of the team's games. */
export type WithdrawGameAction = 'forfeit' | 'double_forfeit' | 'cancel' | 'strike' | 'keep' | 'later' | 'replace'
export const WITHDRAW_ACTIONS = ['forfeit', 'double_forfeit', 'cancel', 'strike', 'keep', 'later', 'replace'] as const satisfies readonly WithdrawGameAction[]
/** What a Reinstate does to a game the withdrawal changed. */
export type ReinstateGameAction = 'reset' | 'reopen' | 'restore' | 'keep' | 'put_back'
export const REINSTATE_ACTIONS = ['reset', 'reopen', 'restore', 'keep', 'put_back'] as const satisfies readonly ReinstateGameAction[]

export type WithdrawalStepState = 'pending' | 'done' | 'waiting' | 'skipped' | 'failed'

/** The game as the withdrawal found it (for Reinstate and the audit). Public values only. */
export interface WithdrawalGameBefore {
  status: GameStatus
  liveStatus: GameStatus
  score: Score | null
  sets: Score[]
  winnerSide: Side | null
  decidedBy: string | null
  home: string | null
  away: string | null
  /**
   * The result's "Corrected" mark (`games.result.correctedAt`, OV-A5) as the withdrawal found it.
   * Reinstate's restore of a struck result carries it over, so a result phones showed as corrected comes
   * back corrected (D92, ANSWERS-APP-VERIFY-n2 Va2-3). One they showed plain comes back plain when
   * followers already have that result (the quiet restore); when they were told a different one, the
   * restore sends "Corrected final" and 9a marks it, as before D92. Absent on a record written before
   * it: that restore carries no mark (9a's rule alone decides).
   */
  correctedAt?: TimestampLike | null
}

export interface WithdrawalStep {
  gameId: string
  /** "Game 7 · Sat, Oct 31 3:00 PM · Court 2 · vs Y" */
  label: string
  action: WithdrawGameAction
  /** The team put in, for 'replace'. */
  replaceWith: string | null
  /** The withdrawn team's side in this game. */
  side: Side
  /** The other team, when known. */
  opponent: string | null
  /** A losers'-bracket game the withdrawn team drops into when it forfeits this one. */
  drop: string | null
  before: WithdrawalGameBefore
  /**
   * What the step left: compared by Reinstate to tell whether staff changed the game since. `score` and
   * `sets` are the result a forfeit or double forfeit records (absent for a cancel or strike).
   */
  after: { status: GameStatus; winnerSide: Side | null; decidedBy: string | null; score?: Score | null; sets?: Score[] } | null
  state: WithdrawalStepState
  /** A server refusal, or why it was skipped; never personal data. */
  note: string | null
}

/** A non-game step: the pin, the listing and the pushes. */
export interface WithdrawalStage {
  state: WithdrawalStepState
  note: string | null
}

export interface ReinstateStep {
  gameId: string
  label: string
  action: ReinstateGameAction
  state: WithdrawalStepState
  note: string | null
}

/** events/{e}/withdrawals/{teamId}. Function-written; readable by teams.manage, schedule.edit or scores.correct. Never public. */
export interface TeamWithdrawal {
  teamId: string
  divisionId: string
  teamName: string
  /** The standings table key (group letter or NO_GROUP_KEY). */
  table: string | null
  /** 'applying' / 'reinstating' while a run is in progress or was cut off (the next apply resumes it). */
  status: 'applying' | 'withdrawn' | 'reinstating' | 'reinstated'
  /** Staff only, 3–200 characters. */
  reason: string
  /** This withdrawal unlisted a listed team. */
  unlisted: boolean
  /** The place the withdrawal pinned, with the note shown in the app. `at` is the pin's own time. */
  pin: { group: string; place: number; note: string; at: TimestampLike | null } | null
  /** The team's pin before the withdrawal (Lane B shape), put back by Reinstate. */
  pinBefore: StandingsPin | null
  /** Announcements queued. */
  notified: number
  /** Games whose other team has been told (a retry never tells a team twice). */
  notifiedGames?: string[]
  /** Steps and stages of the latest run (the withdrawal, or once reinstated the reinstatement) that failed. */
  problems: number
  /** At most 40, in start-time order. */
  steps: WithdrawalStep[]
  stages: { pin: WithdrawalStage; listing: WithdrawalStage; notify: WithdrawalStage }
  /** Choices made at the apply (placeLast, unlist, notify, publicNote). */
  options: { placeLast: boolean; unlist: boolean; notify: boolean; publicNote: string }
  at: TimestampLike | null
  by: string
  byName: string
  reinstatedAt: TimestampLike | null
  reinstatedBy: string | null
  reinstatedByName: string | null
  reinstateReason: string | null
  reinstateSteps: ReinstateStep[]
  reinstateStages: { pin: WithdrawalStage; listing: WithdrawalStage; notify: WithdrawalStage } | null
  reinstateOptions: { removePin: boolean; relist: boolean; notify: boolean } | null
  /** At most 3 earlier rounds, newest first. */
  history: Omit<TeamWithdrawal, 'history'>[]
  updatedAt: TimestampLike | null
}
